/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { get, startCase, toString } from 'lodash';
import { useParams } from 'react-router-dom';
import { AlertBar, BrandColors } from '@blend/enterprise-components';
import { CenteredLoadingSpinner } from 'blb-components/CenteredLoadingSpinner';
import { getDebugDataByLoanId } from 'blb-api/loans';
import { FullDebugDataResponse } from 'blb-types/internal/api/loans';

import { JSONTreeView } from './JSONTreeView';
import { DashboardSection } from './DashboardSection';
import { SendENoteSigningLinkEmail } from 'blb-components/AuthenticatedApp/DashboardPage/Support/SendENoteSigningLinkEmail';
import { AutoTaggingAudit } from './DebugTab/AutoTaggingAudit';
import { AuditEventTable } from 'blb-components/AuthenticatedApp/DashboardPage/DebugTab/AuditEventTable';
import { DashboardSectionItem } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSectionItem';

export const styles = StyleSheet.create({
  snippet: {
    backgroundColor: BrandColors.CLOUD_20,
    borderRadius: 4,
    maxWidth: 700,
    padding: 8,
  },
});

export namespace DebugTab {
  export interface Props {
    tenant: string;
  }
}

// TODO: Maybe useMemo so we don't repeat the same request?

export const DebugTab: React.FC<DebugTab.Props> = ({ tenant }) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>();
  const [debugData, setDebugData] = useState<FullDebugDataResponse>();
  const { loanId } = useParams<{ loanId: string }>();

  const fetchDebugData = async () => {
    try {
      setLoading(true);
      const { data } = await getDebugDataByLoanId(loanId, tenant);
      setDebugData(data);
    } catch (err) {
      setError(get(err, 'message', toString(err)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchDebugData();
  }, []);

  if (error) {
    return <AlertBar id={`DebugTab-error`} message={error} theme="error" />;
  }

  if (isLoading || debugData === undefined) {
    return <CenteredLoadingSpinner />;
  }

  const debugSectionConfigs: DashboardSectionItem.SectionConfig[] = Object.keys(debugData)
    .filter(key => key !== 'auditEvents')
    .map(key => ({
      content: (
        <pre className={css(styles.snippet)} id={`it-${key}-debugger`}>
          <JSONTreeView data={(debugData as Record<string, any>)[key]} />
        </pre>
      ),
      title: startCase(key),
    }));

  const auditEvents = get(debugData, 'auditEvents') ?? [];
  const auditEventSectionConfig: DashboardSectionItem.SectionConfig = {
    title: 'Audit Events',
    content: <AuditEventTable auditEvents={auditEvents} />,
    collapsible: true,
  };
  debugSectionConfigs.push(auditEventSectionConfig);

  const autoTaggingSectionConfig: DashboardSectionItem.SectionConfig = {
    title: 'Auto Tagging Failures',
    content: <AutoTaggingAudit auditEvents={auditEvents} />,
    collapsible: true,
  };
  debugSectionConfigs.push(autoTaggingSectionConfig);

  const supportSectionConfigs = [
    {
      content: <SendENoteSigningLinkEmail closingId={debugData.fullClosing.id} tenant={tenant} />,
      title: 'E-Note',
    },
  ];

  return (
    <>
      <DashboardSection data-it="debug-section" sectionConfigs={debugSectionConfigs} sectionHeader="Debug" />
      {debugData?.fullClosing && (
        <DashboardSection sectionConfigs={supportSectionConfigs} sectionHeader="Support Operations" />
      )}
    </>
  );
};
